import React, { 
  useCallback, 
  useEffect, 
  useRef, 
  useState 
} from 'react';
import { 
  Button, 
  Select, 
  Spin, 
  Typography 
} from 'antd';
import {
  listPeople,
  listPlatformPeople,
  PeoplePage,
  PeoplePurpose,
  Person
} from '../../../service/people.service';

export type PickerScope =
  | { kind: 'node'; nodeId: number; purpose: PeoplePurpose }
  | { kind: 'platform' };

/** A chosen person (their id) or a typed email that is not an account yet. */
export type PickerValue = string | { email: string };

interface BaseProps {
  scope: PickerScope;
  allowNewEmail?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Called for each person loaded, so a caller can map a chosen id back to an email. */
  onPersonKnown?: (person: Person) => void;
  /** People the picker can label even when not on the loaded page (e.g. the current user). */
  knownPeople?: Person[];
  /** Ids left out of the options (they stay labelled through knownPeople). */
  excludeIds?: string[];
}

export type PersonPickerProps =
  | (BaseProps & {
      mode: 'single';
      value?: PickerValue | null;
      onChange?: (value: PickerValue | undefined) => void;
    })
  | (BaseProps & {
      mode: 'multiple';
      value?: PickerValue[];
      onChange?: (value: PickerValue[]) => void;
    });

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 300;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_PREFIX = 'email:';

const keyOf = (value: PickerValue) =>
  typeof value === 'string' ? value : `${EMAIL_PREFIX}${value.email}`;

const fromKey = (key: string): PickerValue =>
  key.startsWith(EMAIL_PREFIX) ? { 
    email: key.slice(EMAIL_PREFIX.length) 
  } : key;

const PersonLabel: React.FC<{ person: Person }> = ({ person }) => (
  <span>
    {person.name && person.name !== person.email ? `${person.name} ` : ''}
    <Typography.Text type="secondary">{person.email}</Typography.Text>
    {person.source ? (
      <Typography.Text 
        type="secondary"
      > ({person.source.replace(/_/g, ' ')})</Typography.Text>
    ) : null}
  </span>
);

const PersonPicker: React.FC<PersonPickerProps> = (props) => {
  const { scope, mode, allowNewEmail, disabled, placeholder, onPersonKnown, knownPeople, excludeIds } = props;
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const known = useRef<Record<string, Person>>({});
  const requestId = useRef(0);
  (knownPeople || []).forEach((p) => {
    known.current[p.id] = p;
  });

  const nodeId = scope.kind === 'node' ? scope.nodeId : undefined;
  const purpose = scope.kind === 'node' ? scope.purpose : undefined;
  // A stable key so a new array with the same ids does not refetch.
  const excludeKey = (excludeIds || []).join(',');

  // Debounce the typed term into the term that is searched.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPage = useCallback(
    (offset: number): Promise<PeoplePage> => {
      const query = { search: term, limit: PAGE_SIZE, offset };
      return nodeId !== undefined && purpose
        ? listPeople(nodeId, purpose, {
          ...query,
          exclude: excludeKey ? excludeKey.split(',') : undefined
        })
        : listPlatformPeople(query);
    },
    [nodeId, purpose, term, excludeKey]
  );

  const load = useCallback(
    (offset: number) => {
      const id = ++requestId.current;
      setLoading(true);
      setError(false);
      fetchPage(offset)
        .then((page) => {
          if (id !== requestId.current) return; // a newer request superseded this one
          page.people.forEach((p) => {
            known.current[p.id] = p;
            onPersonKnown?.(p);
          });
          setPeople((prev) => (offset === 0 ? page.people : [...prev, ...page.people]));
          setTotal(page.total);
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setError(true);
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchPage]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const typed = search.trim();
  const showInvite =
    !!allowNewEmail &&
    EMAIL_RE.test(typed) &&
    !people.some((p) => p.email.toLowerCase() === typed.toLowerCase());

  // Node scope excludes on the server; the platform list has no such parameter.
  const options: any[] = people
    .filter((p) => nodeId !== undefined || !(excludeIds || []).includes(p.id))
    .map((p) => ({
      value: p.id,
      label: <PersonLabel person={p} />,
      plain: `${p.name} ${p.email}`
    }));
  if (showInvite) {
    options.unshift({
      value: `${EMAIL_PREFIX}${typed}`,
      label: `Invite ${typed}`,
      plain: typed
    });
  }

  const labelFor = (value: PickerValue) => {
    if (typeof value !== 'string') return `Invite ${value.email}`;
    const person = known.current[value];
    return person ? <PersonLabel person={person} /> : value;
  };

  const toSelectValue = (value: PickerValue) => ({ 
    value: keyOf(value), 
    label: labelFor(value) 
  });

  const selected =
    mode === 'multiple'
      ? ((props.value || []) as PickerValue[]).map(toSelectValue)
      : props.value
        ? toSelectValue(props.value as PickerValue)
        : undefined;

  const handleChange = (next: any) => {
    if (mode === 'multiple') {
      (props.onChange as (v: PickerValue[]) => void | undefined)?.(
        (next || []).map((item: any) => fromKey(item.value))
      );
    } else {
      (props.onChange as (v: PickerValue | undefined) => void | undefined)?.(
        next ? fromKey(next.value) : undefined
      );
    }
    setSearch('');
  };

  const hasMore = people.length < total;

  return (
    <Select
      showSearch
      labelInValue
      allowClear
      mode={mode === 'multiple' ? 'multiple' : undefined}
      disabled={disabled}
      placeholder={placeholder}
      filterOption={false}
      searchValue={search}
      onSearch={setSearch}
      value={selected as any}
      onChange={handleChange}
      options={options}
      loading={loading}
      style={{ width: '100%' }}
      notFoundContent={
        loading ? (
          <Spin size="small" />
        ) : error ? (
          <Typography.Text 
            type="danger"
          >Could not load people</Typography.Text>
        ) : (
          'No people found'
        )
      }
      popupRender={(menu) => (
        <>
          {error && people.length > 0 && (
            <div style={{ padding: '4px 12px' }}>
              <Typography.Text 
                type="danger"
              >Could not load people</Typography.Text>
            </div>
          )}
          {menu}
          {hasMore && (
            <div style={{ padding: 4, textAlign: 'center' }}>
              <Button
                type="link"
                size="small"
                loading={loading}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => load(people.length)}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    />
  );
};

export default PersonPicker;
